import { useEffect, useState } from 'react';
import { Camera, PencilLine, Calculator } from 'lucide-react';

import { KioskLoadCards } from './KioskLoadCards';
import { KioskReadingsList } from './KioskReadingsList';
import { QrCameraScanner } from './QrCameraScanner';
import {
  formatDate,
  formatReadingSource,
  formatWeight,
  readingLabel,
  scaleReadingLabel,
  statusClass,
} from './kioskFormatters';

export function KioskCurrentWeighingPanel({
  confirmState,
  createState,
  deviceMap,
  disableDecisionButtons,
  displayedCanConfirm,
  displayedCanReject,
  displayedCurrentRequest,
  displayedReadings,
  displayedReadingsState,
  displayedRequestLoad,
  latestScaleReading,
  scannerErrorMessage,
  latestScannerProductLabel,
  latestScannerReading,
  latestScannerStoredQuantity,
  onConfirm,
  onCameraQrDetected,
  onManualWeightSubmit,
  onQrWeightLookup,
  onCreateRequest,
  onNotesChange,
  onReturnQuantityChange,
  onOpenDetail,
  onReject,
  operatorNotes,
  returnQuantityPacks,
  returnQuantityPacksMax,
  registerOperationBlocker,
  rejectState,
  replacedFromLoadName,
  requests,
  selectedRegisterId,
  selectedRequestDirection,
  selectedRequestLoadId,
  selectedBinding,
}) {
  const [isCameraOpen, setIsCameraOpen] = useState(false);
  const [isManualWeightOpen, setIsManualWeightOpen] = useState(false);
  const [manualWeight, setManualWeight] = useState('');
  const [qrWeightState, setQrWeightState] = useState({ type: 'idle', message: '' });
  const scannerCalculatedGoodsWeight = latestScannerReading?.scannedProduct?.calculatedGoodsWeightKg;
  const scannerPalletWeight = latestScannerReading?.scannedProduct?.palletWeight;
  const scannerCalculatedGoodsWeightKg = Number(scannerCalculatedGoodsWeight);
  const scannerPalletWeightKg = Number(scannerPalletWeight);
  const hasScannerCalculatedWeight = scannerCalculatedGoodsWeight != null
    && scannerPalletWeight != null
    && Number.isFinite(scannerCalculatedGoodsWeightKg)
    && Number.isFinite(scannerPalletWeightKg);
  const scannerCalculatedWeightKg = scannerCalculatedGoodsWeightKg + scannerPalletWeightKg;

  useEffect(() => {
    setIsManualWeightOpen(false);
    setManualWeight('');
    setQrWeightState({ type: 'idle', message: '' });
  }, [displayedCurrentRequest?.requestId, latestScannerReading?.id]);

  async function handleQrWeightLookup() {
    setQrWeightState({ type: 'pending', message: 'กำลังค้นหาน้ำหนักจาก QR...' });
    try {
      await onQrWeightLookup();
      setQrWeightState({ type: 'success', message: 'บันทึกน้ำหนักจาก QR แล้ว' });
    } catch (error) {
      setQrWeightState({
        type: 'error',
        message: error instanceof Error ? error.message : 'ไม่สามารถค้นหาน้ำหนักจาก QR ได้',
      });
    }
  }

  async function handleManualWeight(event) {
    event.preventDefault();
    const weightKg = Number(manualWeight);
    if (!Number.isFinite(weightKg) || weightKg <= 0) {
      setQrWeightState({ type: 'error', message: 'กรุณากรอกน้ำหนักที่มากกว่า 0 กิโลกรัม' });
      return;
    }

    setQrWeightState({ type: 'pending', message: 'กำลังบันทึกน้ำหนัก...' });
    try {
      await onManualWeightSubmit(weightKg);
      setIsManualWeightOpen(false);
      setManualWeight('');
      setQrWeightState({ type: 'success', message: 'บันทึกน้ำหนักแล้ว' });
    } catch (error) {
      setQrWeightState({
        type: 'error',
        message: error instanceof Error ? error.message : 'ไม่สามารถบันทึกน้ำหนักได้',
      });
    }
  }

  return (
    <div className="kiosk-stack">
      <div className="kiosk-card">
        <div>
          <div className="kiosk-register-top">
            <h3>การชั่งน้ำหนักปัจจุบัน:</h3>
            <p>
              {displayedCurrentRequest
                ? displayedCurrentRequest.requestId
                : 'ยังไม่มีคำขอการชั่งสำหรับสถานีนี้'}
            </p>
            <span className={`kiosk-pill kiosk-pill-${statusClass(displayedCurrentRequest?.requestStatus ?? 'idle')}`}>
              {displayedCurrentRequest?.requestStatus ?? 'ยังไม่มีการชั่งน้ำหนัก'}
            </span>
          </div>
          {displayedCurrentRequest?.replacesRequestId ? (
            <p className="kiosk-void-replacement-notice">ย้ายมาจาก {replacedFromLoadName ?? '—'}</p>
          ) : null}
          <br />
        </div>

        <div className="kiosk-reading-grid">
          <article className="kiosk-reading-card">
            <div className="kiosk-reading-top kiosk-scanner-reading-top">
              <span className="kiosk-reading-tag">เครื่องสแกน</span>
              <button
                type="button"
                className="kiosk-camera-button"
                disabled={!displayedCurrentRequest}
                onClick={() => setIsCameraOpen(true)}
                aria-label="สแกน QR ด้วยกล้อง"
              >
                <Camera size={18} aria-hidden="true" />
                กล้อง
              </button>
            </div>
            <div className="kiosk-reading-value">{readingLabel(latestScannerReading)}</div>
            {latestScannerProductLabel ? (
              <div style={{ boxShadow: '0 4px 8px 0 rgba(0,0,0,0.2)', backgroundColor: '#eaf8ed', borderRadius: '0.5rem' }}>
                <p style={{ padding: '10px' }}>
                  {latestScannerProductLabel}
                  {latestScannerStoredQuantity == null ? '' : ` (${latestScannerStoredQuantity} แพ็ค)`}
                </p>
              </div>
            ) : null}
            <p className="kiosk-reading-time">{formatDate(latestScannerReading?.occurredAtUtc)}</p>
            <p className="kiosk-muted">{latestScannerReading?.deviceId || selectedBinding?.scannerDeviceId || 'ไม่ระบุ'}</p>
            {scannerErrorMessage ? (
              <p className="kiosk-reading-error" role="alert">{scannerErrorMessage}</p>
            ) : null}
          </article>

          <article className="kiosk-reading-card">
            <div className="kiosk-reading-top kiosk-scale-reading-top">
              <span className="kiosk-reading-tag">เครื่องชั่ง</span>
              <div className="kiosk-weight-actions">
                <button
                  type="button"
                  className="kiosk-camera-button"
                  disabled={
                    !displayedCurrentRequest
                    || displayedCurrentRequest.requestDirection === 'Inbound'
                    || !latestScannerReading
                    || qrWeightState.type === 'pending'
                  }
                  onClick={handleQrWeightLookup}
                  aria-label="ค้นหาน้ำหนักจาก QR"
                  title={displayedCurrentRequest?.requestDirection === 'Inbound'
                    ? 'คำขอขาเข้าต้องใช้น้ำหนักจากเครื่องชั่ง'
                    : undefined}
                >
                  <Calculator size={18} aria-hidden="true" />
                  ใช้คำนวณ
                </button>
                <button
                  type="button"
                  className="kiosk-camera-button kiosk-manual-weight-button"
                  disabled={!displayedCurrentRequest || qrWeightState.type === 'pending'}
                  onClick={() => setIsManualWeightOpen((current) => !current)}
                  aria-expanded={isManualWeightOpen}
                  aria-controls="kiosk-manual-weight-form"
                >
                  <PencilLine size={15} aria-hidden="true" />
                  กรอกเอง
                </button>
              </div>
            </div>
            {isManualWeightOpen ? (
              <form id="kiosk-manual-weight-form" className="kiosk-manual-weight-form" onSubmit={handleManualWeight}>
                <label htmlFor="kiosk-manual-weight">น้ำหนัก (กก.)</label>
                <input
                  id="kiosk-manual-weight"
                  type="number"
                  inputMode="decimal"
                  min="0.001"
                  step="0.001"
                  value={manualWeight}
                  onChange={(event) => setManualWeight(event.target.value)}
                  autoFocus
                  required
                />
                <button type="submit" className="kiosk-primary-btn" disabled={qrWeightState.type === 'pending'}>
                  บันทึก
                </button>
              </form>
            ) : null}
            {hasScannerCalculatedWeight ? (
              <div className="kiosk-calculated-weight-label">
                น้ำหนักคำนวณ {formatWeight(scannerCalculatedWeightKg)}
              </div>
            ) : null}
            <div className="kiosk-reading-value">{scaleReadingLabel(latestScaleReading)}</div>
            <p className="kiosk-reading-time">{formatDate(latestScaleReading?.occurredAtUtc)}</p>
            <p className="kiosk-muted">
              {latestScaleReading?.deviceId || selectedBinding?.scaleDeviceId || 'ไม่ระบุ'}
              {formatReadingSource(latestScaleReading?.readingSource)
                ? ` · ${formatReadingSource(latestScaleReading.readingSource)}`
                : ''}
            </p>
            {qrWeightState.message ? (
              <p className={`kiosk-feedback kiosk-feedback-${qrWeightState.type}`} role="status">
                {qrWeightState.message}
              </p>
            ) : null}
          </article>
        </div>

        <div className="kiosk-confirm-panel">
          <div className="kiosk-confirm-copy">
            <div className="kiosk-selected-load-heading">
              <h3>ใบจองที่เลือก:</h3>
              <span
                className={`kiosk-direction-status kiosk-direction-status-${selectedRequestDirection.toLowerCase()}`}
                aria-label={`ทิศทางคำขอ: ${selectedRequestDirection === 'Outbound' ? 'ขาออก' : 'ขาเข้า'}`}
              >
                <span className="kiosk-direction-status-dot" aria-hidden="true" />
                {selectedRequestDirection === 'Outbound' ? 'ขาออก' : 'ขาเข้า'}
              </span>
            </div>
            {displayedCurrentRequest ? (
              <div className="kiosk-selected-load-panel">
                <p className="kiosk-hint">
                  {displayedCurrentRequest.requestLoadId || selectedRequestLoadId
                    ? ''
                    : 'เลือกโหลดก่อน หรือเลือกหลังจากกดยืนยัน.'}
                </p>
                {displayedRequestLoad ? (
                  <KioskLoadCards
                    requestLoads={[displayedRequestLoad]}
                    requests={requests}
                    selectedLoadId={displayedRequestLoad.loadId}
                    selectedLoadDirection={selectedRequestDirection}
                    onOpenDetail={onOpenDetail}
                  />
                ) : null}
              </div>
            ) : null}
            <br />
            {!displayedCurrentRequest && !registerOperationBlocker ? (
              <div className="kiosk-actions">
                <button type="button" className="kiosk-secondary-btn" disabled={createState.type === 'pending'} onClick={onCreateRequest}>
                  เตรียมคำขอการชั่ง
                </button>
              </div>
            ) : null}
            {createState.message ? (
              <p className={`kiosk-feedback kiosk-feedback-${createState.type}`}>{createState.message}</p>
            ) : null}
          </div>

          <div className="kiosk-confirm-form">
            {selectedRequestDirection === 'Inbound' ? (
              <label className="kiosk-field">
                <span>จำนวนแพ็คที่รับคืน</span>
                <input
                  type="number"
                  name="returnQuantityPacks"
                  aria-label="จำนวนแพ็คที่รับคืน"
                  value={returnQuantityPacks}
                  onChange={onReturnQuantityChange}
                  min="1"
                  max={returnQuantityPacksMax ?? undefined}
                  step="1"
                  required
                />
                {returnQuantityPacksMax == null ? null : (
                  <small className="kiosk-hint">สูงสุด {returnQuantityPacksMax} แพ็ค</small>
                )}
              </label>
            ) : null}
            <label className="kiosk-field">
              <span>หมายเหตุการตัดสินใจ</span>
              <textarea
                name="confirmationNotes"
                value={operatorNotes}
                onChange={onNotesChange}
                placeholder="หมายเหตุเพิ่มเติมสำหรับการยืนยันหรือปฏิเสธ"
                rows={3}
              />
            </label>
            <div className="kiosk-actions">
              <button type="button" className="kiosk-primary-btn" disabled={!displayedCanConfirm || disableDecisionButtons || confirmState.type === 'pending'} onClick={onConfirm}>
                ยืนยัน
              </button>
              <button type="button" className="kiosk-danger-btn" disabled={!displayedCanReject || disableDecisionButtons || rejectState.type === 'pending'} onClick={onReject}>
                ปฏิเสธ
              </button>
            </div>
            {disableDecisionButtons ? (
              <p className="kiosk-hint">ไม่มีข้อมูลการอ่านและยังไม่ได้เลือกรอบการขนส่ง จึงยังไม่สามารถยืนยันหรือปฏิเสธได้</p>
            ) : !displayedCanConfirm ? (
              <p className="kiosk-hint">การยืนยันต้องได้รับค่าเครื่องชั่งและเครื่องสแกนครบถ้วน การปฏิเสธสามารถทำได้จนกว่าคำขอจะเสร็จสิ้น</p>
            ) : null}
            {confirmState.message ? <p className={`kiosk-feedback kiosk-feedback-${confirmState.type}`}>{confirmState.message}</p> : null}
            {rejectState.message ? <p className={`kiosk-feedback kiosk-feedback-${rejectState.type}`}>{rejectState.message}</p> : null}
          </div>
        </div>
      </div>

      <div className="kiosk-card">
        <div className="kiosk-card-header">
          <div>
            <h2>รายการอ่านค่า</h2>
            <p>การอ่านจากเครื่องสแกนและเครื่องชั่งสำหรับคำขอนี้ เรียงจากใหม่ไปเก่า</p>
          </div>
        </div>
        <KioskReadingsList readings={displayedReadings} readingsState={displayedReadingsState} deviceMap={deviceMap} />
      </div>

      <QrCameraScanner
        isOpen={isCameraOpen}
        onClose={() => setIsCameraOpen(false)}
        onDetected={onCameraQrDetected}
      />
    </div>
  );
}
